import { createSlice, PayloadAction, createAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { ErrorType } from "../../types/errorType";
import { API_ENDPOINTS } from "Config/apiConfig";
import {
  AppTypeWithData,
  initialState,
  createFetchInitialDataThunk,
  createFetchProgressiveDataThunk,
  ApiResponse,
} from "../../utils/fetchDataUtils";
import { IncidentType } from "types/incidentType";

// Initial state
const initialIncidentsState: AppTypeWithData<IncidentType> = initialState<IncidentType>();

// Action creators
export const appendDataAction = createAction<IncidentType[]>("incidentList/appendData");
export const setProgressAction = createAction<number>("incidentList/setProgress");

// Reducer functions
const appendData = (state: AppTypeWithData<IncidentType>, action: PayloadAction<IncidentType[]>) => {
  const uniqueRecords = action.payload.filter(
    (newRecord) => !state.data.some((existingRecord) => existingRecord.id === newRecord.id)
  );
  state.data.push(...uniqueRecords); // Append only unique records
};

const setProgress = (state: AppTypeWithData<IncidentType>, action: PayloadAction<number>) => {
  state.progress = action.payload;
};

// Thunks for fetching data
export const fetchInitialData = createFetchInitialDataThunk<IncidentType>("incident", API_ENDPOINTS.incidents);

export const fetchProgressiveData = createFetchProgressiveDataThunk<IncidentType>(
  "incidents",
  API_ENDPOINTS.incidents,
  (state: RootState) => state.incidentList.totalRecords,
  (state: RootState) => state.incidentList.data.length,
  appendDataAction,
  setProgressAction
);

// Slice definition
const incidentListSlice = createSlice({
  name: "incidentList",
  initialState: initialIncidentsState,
  reducers: {
    appendData,
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setProgress,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInitialData.fulfilled, (state, action: PayloadAction<ApiResponse<IncidentType>>) => {
      state.data = action.payload.results;
      state.totalRecords = action.payload.totalRecords;
      state.totalPages = action.payload.totalPages;
      state.page = action.payload.page;
      state.loading = false;
      state.isInitialLoadComplete = true;
    });
    builder.addCase(fetchInitialData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.loading = false;
    });
    builder.addCase(fetchProgressiveData.pending, (state) => {
      state.progressiveLoading = true;
      state.progress = 0;
      state.error = null;
    });
    builder.addCase(fetchProgressiveData.fulfilled, (state) => {
      state.progressiveLoading = false;
      state.progress = 100;
    });
    builder.addCase(fetchProgressiveData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.progressiveLoading = false;
    });
  },
});

// Export actions and reducer
export const { setPageSize, setCurrentPage } = incidentListSlice.actions;
export const incidentRootState = (state: RootState) => state.incidentList;
export default incidentListSlice.reducer;
